/* ==========================================================================
   Untapped - Native wishlist (v2, Oct 2026)
   No third-party app. Saves to localStorage (per device/browser).

     <wishlist-button>  bookmark toggle for product cards / PDP
     <wishlist-count>   live badge (account dropdown)
     <wishlist-page>    renders saved products on /pages/wishlist

   v2 changes:
   - Wishlist page shows size chips per product. "Add to bag" only works
     once a size is chosen (it used to silently add the first size, S).
   - Prices always come from live product data in the shopper's current
     currency. A stored price is only shown if it was saved in the same
     currency, so there is no flash of GBP for INR shoppers.
   - Full Shopify money-format support (thousand separators, all
     amount_* placeholders).
   - Add to bag uses Horizon's CartLinesUpdateEvent so the cart drawer
     opens and the cart count updates, same as the product page.
   - Toast feedback when saving / removing, with a link to the wishlist.
   - Real alt text, responsive images, locale-aware product URLs.
   ========================================================================== */

(() => {
  const STORAGE_KEY = 'untapped:wishlist:v1';
  const CHANGE_EVENT = 'wishlist:change';
  const WISHLIST_URL = '/pages/wishlist';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currentCurrency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';

  /* ---------------------------------------------------------------------- */
  const Store = {
    read() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    },
    write(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        /* storage full / disabled */
      }
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
    },
    has(id) {
      return this.read().some((item) => String(item.id) === String(id));
    },
    add(item) {
      const items = this.read();
      if (items.some((i) => String(i.id) === String(item.id))) return items;
      items.unshift({ ...item, addedAt: Date.now() });
      this.write(items);
      return items;
    },
    remove(id) {
      const items = this.read().filter((i) => String(i.id) !== String(id));
      this.write(items);
      return items;
    },
    toggle(item) {
      return this.has(item.id) ? this.remove(item.id) : this.add(item);
    },
    count() {
      return this.read().length;
    },
  };

  window.UntappedWishlist = Store;

  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items: Store.read() } }));
    }
  });

  /* ---------------------------------------------------------------------- */
  /* Helpers                                                                 */
  /* ---------------------------------------------------------------------- */
  const escapeHtml = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));

  function formatMoney(cents, format) {
    const fmt = format || '{{amount}}';
    const value = Number(cents);
    if (!isFinite(value)) return '';

    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    };

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(value, 0, ' ');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator':
          return withDelimiters(value, 2, ' ', '.');
        case 'amount':
        default:
          return withDelimiters(value, 2);
      }
    });
  }

  function sizedImage(url, width) {
    if (!url) return '';
    if (/[?&]width=/.test(url)) return url;
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  }

  /* Toast ----------------------------------------------------------------- */
  let toastEl = null;
  let toastTimer = null;
  function toast(message, withLink) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML =
      `<span class="wishlist-toast__text">${escapeHtml(message)}</span>` +
      (withLink && !location.pathname.endsWith(WISHLIST_URL)
        ? `<a class="wishlist-toast__link" href="${root.replace(/\/$/, '')}${WISHLIST_URL}">View wishlist</a>`
        : '');
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3200);
  }

  /* Cart add (Horizon events, with plain-event fallback) ------------------ */
  async function addToCart(variantId, sourceEl) {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }

    let deferred = null;
    if (events && events.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      sourceEl.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'wishlist',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to your bag.');

      const cart = await (await fetch(`${root}cart.js`)).json();

      if (deferred) {
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'wishlist',
            sourceId: sourceEl.id || 'wishlist',
            itemCount: 1,
            sections: data.sections,
            didError: false,
          },
        });
      } else {
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      }

      document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble').forEach((el) => {
        el.textContent = cart.item_count;
      });
      return true;
    } catch (err) {
      if (deferred) {
        deferred.reject(err);
        if (events.CartErrorEvent) {
          sourceEl.dispatchEvent(new events.CartErrorEvent({ error: err.message, code: 'SERVICE_UNAVAILABLE' }));
        }
      }
      throw err;
    }
  }

  /* Icons ----------------------------------------------------------------- */
  const MARK_PATH =
    'M6 3.75h12a.75.75 0 0 1 .75.75v15.19a.6.6 0 0 1-.93.5L12 16.5l-5.82 3.69a.6.6 0 0 1-.93-.5V4.5a.75.75 0 0 1 .75-.75Z';
  const MARK_OUTLINE = `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="${MARK_PATH}" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" fill="none"/></svg>`;
  const MARK_FILLED = `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${MARK_PATH}" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>`;

  /* ----------------------------------------------------------------------
     <wishlist-button>
     ---------------------------------------------------------------------- */
  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.dataset.ready = '';
      const cents = this.getAttribute('data-product-price-cents');
      this._product = {
        id: this.getAttribute('data-product-id'),
        handle: this.getAttribute('data-product-handle'),
        title: this.getAttribute('data-product-title') || '',
        url: this.getAttribute('data-product-url') || '',
        image: this.getAttribute('data-product-image') || '',
        price: this.getAttribute('data-product-price') || '',
        priceCents: cents != null && cents !== '' ? Number(cents) : null,
        currency: this.getAttribute('data-currency') || currentCurrency(),
      };

      this._button = this.querySelector('button');
      if (!this._button) {
        this._button = document.createElement('button');
        this._button.type = 'button';
        this._button.className = 'wishlist-button__btn';
        this.appendChild(this._button);
      }

      this._button.addEventListener('click', this._onClick);
      this._onChange = () => this._render();
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._render();
    }

    disconnectedCallback() {
      this._button?.removeEventListener('click', this._onClick);
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const wasSaved = Store.has(this._product.id);
      Store.toggle({ ...this._product, currency: currentCurrency() });
      toast(wasSaved ? 'Removed from wishlist' : 'Saved to wishlist', !wasSaved);
    };

    _render() {
      if (!this._product.id) return;
      const active = Store.has(this._product.id);
      const withLabel = this.hasAttribute('data-with-label');
      const labelIdle = this.getAttribute('data-label') || 'Save';
      const labelActive = this.getAttribute('data-label-active') || 'Saved';
      const a11yLabel = active ? 'Remove from wishlist' : 'Save to wishlist';
      const icon = `<span class="wishlist-button__icon">${active ? MARK_FILLED : MARK_OUTLINE}</span>`;
      const text = withLabel
        ? `<span class="wishlist-button__text">${active ? labelActive : labelIdle}</span>`
        : '';
      this._button.innerHTML = icon + text;
      this._button.setAttribute('aria-pressed', String(active));
      this._button.setAttribute('aria-label', a11yLabel);
      this._button.setAttribute('title', a11yLabel);
      this.classList.toggle('is-active', active);
    }
  }

  /* ----------------------------------------------------------------------
     <wishlist-count>
     ---------------------------------------------------------------------- */
  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this._hideEmpty = this.hasAttribute('data-hide-empty');
      this._onChange = () => this._render();
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
    }

    _render() {
      const n = Store.count();
      this.textContent = n > 0 ? String(n) : this._hideEmpty ? '' : '0';
      this.toggleAttribute('hidden', this._hideEmpty && n === 0);
      this.dataset.count = String(n);
    }
  }

  /* ----------------------------------------------------------------------
     <wishlist-page>
     ---------------------------------------------------------------------- */
  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.id = this.id || 'wishlist-page';
      this._grid = this.querySelector('[data-wishlist-grid]') || this;
      this._empty = this.querySelector('[data-wishlist-empty]');
      this._moneyFormat = this.getAttribute('data-money-format') || '{{amount}}';
      this._products = new Map(); // id -> fresh product data
      this._selected = new Map(); // id -> chosen variant id
      this._onChange = () => this._render();
      document.addEventListener(CHANGE_EVENT, this._onChange);
      this._grid.addEventListener('click', this._onClick);
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this._onChange);
      this._grid.removeEventListener('click', this._onClick);
    }

    async _render() {
      const items = Store.read();
      this.toggleAttribute('data-empty', items.length === 0);
      if (this._empty) this._empty.toggleAttribute('hidden', items.length > 0);
      if (items.length === 0) {
        this._grid.innerHTML = '';
        return;
      }

      // First paint: stored data (price only if saved in the same currency).
      this._grid.innerHTML = items.map((item) => this._cardMarkup(item, this._products.get(String(item.id)))).join('');

      const missing = items.filter((item) => item.handle && !this._products.has(String(item.id)));
      if (!missing.length) return;

      const gone = [];
      await Promise.all(
        missing.map(async (item) => {
          try {
            const res = await fetch(`${root}products/${encodeURIComponent(item.handle)}.js`, {
              headers: { Accept: 'application/json' },
            });
            if (res.status === 404) {
              gone.push(String(item.id));
              return;
            }
            if (!res.ok) return;
            this._products.set(String(item.id), await res.json());
          } catch (e) {
            /* network error: keep stored data */
          }
        })
      );

      if (gone.length) {
        Store.write(Store.read().filter((i) => !gone.includes(String(i.id))));
        return; // CHANGE_EVENT triggers a fresh render
      }

      const latest = Store.read();
      this._grid.innerHTML = latest.map((item) => this._cardMarkup(item, this._products.get(String(item.id)))).join('');
    }

    _sizeOptionIndex(data) {
      if (!data || !Array.isArray(data.options)) return -1;
      return data.options.findIndex((o) => /^size$/i.test(typeof o === 'string' ? o : o.name));
    }

    _cardMarkup(item, data) {
      const id = String(item.id);
      const title = data ? data.title : item.title;
      const url = data ? data.url : item.url;
      const image = data ? data.featured_image || item.image : item.image;

      let price = '';
      if (data) {
        price = formatMoney(data.price, this._moneyFormat);
        if (data.compare_at_price && data.compare_at_price > data.price) {
          price = `<s class="wishlist-card__compare">${formatMoney(data.compare_at_price, this._moneyFormat)}</s> ${price}`;
        }
      } else if (item.priceCents != null && item.currency && item.currency === currentCurrency()) {
        price = formatMoney(item.priceCents, this._moneyFormat);
      } else if (item.price && item.currency && item.currency === currentCurrency()) {
        price = escapeHtml(item.price);
      }

      const img = image
        ? `<img src="${sizedImage(image, 600)}" srcset="${sizedImage(image, 400)} 400w, ${sizedImage(image, 600)} 600w, ${sizedImage(image, 900)} 900w" sizes="(min-width: 750px) 25vw, 50vw" alt="${escapeHtml(title)}" loading="lazy" width="600" height="800">`
        : `<span class="wishlist-card__noimg" aria-hidden="true"></span>`;

      return `
        <article class="wishlist-card${data ? '' : ' is-loading'}" data-id="${escapeHtml(id)}">
          <a class="wishlist-card__media" href="${escapeHtml(url || '#')}">${img}</a>
          <button class="wishlist-card__remove" type="button" data-remove="${escapeHtml(id)}"
            aria-label="Remove ${escapeHtml(title)} from wishlist" title="Remove">&times;</button>
          <div class="wishlist-card__body">
            <a class="wishlist-card__title" href="${escapeHtml(url || '#')}">${escapeHtml(title)}</a>
            <span class="wishlist-card__price">${price || '&nbsp;'}</span>
            ${this._purchaseMarkup(id, data)}
          </div>
        </article>`;
    }

    _purchaseMarkup(id, data) {
      if (!data) {
        return `<button class="wishlist-card__add" type="button" disabled>Loading…</button>`;
      }
      if (!data.available) {
        return `<button class="wishlist-card__add" type="button" disabled>Sold out</button>`;
      }

      const variants = data.variants || [];
      if (variants.length <= 1) {
        const v = variants[0];
        return `<button class="wishlist-card__add" type="button" data-add="${v ? v.id : ''}" data-card="${escapeHtml(id)}">Add to bag</button>`;
      }

      const sizeIndex = this._sizeOptionIndex(data);
      const chosen = this._selected.get(id);

      // Products with several variants but no "Size" option: list every variant.
      const chips = variants
        .map((v) => {
          const label = sizeIndex > -1 ? v.options[sizeIndex] : v.title;
          const isChosen = String(chosen) === String(v.id);
          return `<button type="button" class="wishlist-card__size${isChosen ? ' is-selected' : ''}"
              data-size="${v.id}" data-card="${escapeHtml(id)}" aria-pressed="${isChosen}"
              ${v.available ? '' : 'disabled aria-disabled="true"'}>${escapeHtml(label)}</button>`;
        })
        .join('');

      return `
        <fieldset class="wishlist-card__sizes">
          <legend class="wishlist-card__sizes-label">${sizeIndex > -1 ? 'Size' : 'Option'}</legend>
          <div class="wishlist-card__size-list">${chips}</div>
        </fieldset>
        <button class="wishlist-card__add" type="button" data-card="${escapeHtml(id)}"
          ${chosen ? `data-add="${chosen}"` : 'aria-disabled="true" data-needs-size'}>
          ${chosen ? 'Add to bag' : sizeIndex > -1 ? 'Select a size' : 'Select an option'}
        </button>
        <p class="wishlist-card__msg" role="status" aria-live="polite"></p>`;
    }

    _onClick = async (event) => {
      const removeBtn = event.target.closest('[data-remove]');
      if (removeBtn) {
        const id = removeBtn.getAttribute('data-remove');
        this._selected.delete(id);
        Store.remove(id);
        toast('Removed from wishlist', false);
        return;
      }

      const sizeBtn = event.target.closest('[data-size]');
      if (sizeBtn && !sizeBtn.disabled) {
        const id = sizeBtn.getAttribute('data-card');
        const variantId = sizeBtn.getAttribute('data-size');
        const card = sizeBtn.closest('.wishlist-card');
        const isSame = String(this._selected.get(id)) === String(variantId);

        if (isSame) this._selected.delete(id);
        else this._selected.set(id, variantId);

        card.querySelectorAll('[data-size]').forEach((b) => {
          const on = !isSame && b === sizeBtn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', String(on));
        });

        const add = card.querySelector('.wishlist-card__add');
        const msg = card.querySelector('.wishlist-card__msg');
        if (msg) msg.textContent = '';
        if (isSame) {
          add.removeAttribute('data-add');
          add.setAttribute('aria-disabled', 'true');
          add.setAttribute('data-needs-size', '');
          add.textContent = 'Select a size';
        } else {
          add.setAttribute('data-add', variantId);
          add.removeAttribute('aria-disabled');
          add.removeAttribute('data-needs-size');
          add.textContent = 'Add to bag';
        }
        return;
      }

      const addBtn = event.target.closest('.wishlist-card__add');
      if (!addBtn || addBtn.disabled) return;

      const card = addBtn.closest('.wishlist-card');
      const msg = card && card.querySelector('.wishlist-card__msg');

      if (addBtn.hasAttribute('data-needs-size')) {
        if (msg) msg.textContent = 'Please choose a size first.';
        card.classList.remove('needs-size');
        void card.offsetWidth;
        card.classList.add('needs-size');
        return;
      }

      const variantId = addBtn.getAttribute('data-add');
      if (!variantId) return;

      const original = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      try {
        await addToCart(variantId, this);
        addBtn.textContent = 'Added ✓';
        if (msg) msg.textContent = '';
      } catch (e) {
        addBtn.textContent = 'Try again';
        if (msg) msg.textContent = e.message || 'Something went wrong. Please try again.';
      } finally {
        setTimeout(() => {
          addBtn.disabled = false;
          addBtn.textContent = original.trim() === 'Adding…' ? 'Add to bag' : original;
        }, 1800);
      }
    };
  }

  if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
  if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
  if (!customElements.get('wishlist-page')) customElements.define('wishlist-page', WishlistPage);
})();
